import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import flex from 'styles/components/flex.module.scss'
import format from 'date-fns/format'
import { ErrorDescription, MainHeadline, SubCaution, SubDescription } from 'components/Typography'
import { Spacer } from 'components/Utility'
import {
  BusinessHourForm,
  Checkbox,
  DatetimePicker,
  ImagesCrop,
  PostalCodeForm,
  RadioButtonsForm,
  ReleaseTargetForm,
  SelectBox,
  TagCheckbox,
  Textarea,
  TextInput,
} from 'components/Form'
import {
  useCheckbox,
  useDaysOfWeekCheckbox,
  useImageUrls,
  useSelect,
  useSelectCallback,
  useStringChangeEvent,
  useToggleBoolean,
} from 'hooks/form'
import { BlueButton, NavButton, OrangeButton, TextButton } from 'components/Button'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { FormLabel } from 'components/Label'
import { eatingAreaOptions, stateOptions, publishStatusOptions } from 'constants/options'
import { fetchTags } from 'reducks/tag/operations'
import { getTags } from 'reducks/tag/selectors'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { getIsAdmin } from 'reducks/auth/selectors'
import { fetchAddressFromPostalCode } from 'lib/address'
import { initStore, saveStoreInformation } from 'reducks/store/operations'
import { push } from 'connected-react-router'
import { initShop } from 'reducks/shop/operations'
import { isCompleteStoreInformation, isCompleteStoreSettings } from 'lib/judge'
import { isValidPhoneNumber, isValidUrlString } from 'lib/validator'
import { db, FirebaseTimestamp } from 'firebase/index'
import { initStocks } from 'reducks/stock/operations'
import { initBaskets } from 'reducks/basket/operations'
import { openCautionModal } from 'reducks/modal/operations'
import { HolidayCard } from 'components/Card'
import { FEE_RATE_OPTIONS } from 'constants/store'
import { useBusinessTimes, useRemainingDaysOfWeek } from 'hooks/businessHour'
import { parseToBusinessTime, parseToOpenTime } from 'lib/businessHour'
import { initConfirm, saveStoreConfirm } from 'reducks/confirm/operations'
import { useQueryStripeAccountId } from 'hooks/shop'
import { getStripeAccountId } from 'reducks/shop/selectors'
import { StoreInfoError, StoreSettingError } from 'components/Error'
import { Map } from 'components/Map'
import { useStoreGeoSearch } from 'hooks/storeGeoSearch'
import { saveStoreGeoSearchConfirm } from 'reducks/confirm/operations'
import { saveStoreGeoSearch } from 'reducks/storeGeoSearch/operations'
import { StoreStatus } from 'types/store'
import { fetchWebHostnames } from 'repos/store'

type Props = {
  isCreatePage: boolean
  isEventPage: boolean
  isEditedByOwner: boolean
  storeId: string
}

const ShopEditInformationTemplate: FC<Props> = (props) => {
  const { isCreatePage, isEventPage, isEditedByOwner, storeId } = props

  const dispatch = useDispatch()
  const daysOfWeek = new DaysOfWeek()

  const store = useSelector((state: State) => state.confirm.store)
  const tags = useSelector((state: State) => getTags(state))
  const isAdmin = useSelector((state: State) => getIsAdmin(state))
  const stripeAccountIdInStore = useSelector((state: State) => getStripeAccountId(state))
  const stripeAccountId = useQueryStripeAccountId(store.shop_ref)

  const { imageUrls, handleImageUrls, deleteImageUrls, rearrangeImages, setImageUrls } = useImageUrls()
  const [name, setName] = useState<string>('')
  const [nameKana, setNameKana] = useState<string>('')
  const [firstPostalCode, setFirstPostalCode] = useState<string>('')
  const [secondPostalCode, setSecondPostalCode] = useState<string>('')
  const [state, setState] = useState<string>('')
  const [city, setCity] = useState<string>('')
  const [street, setStreet] = useState<string>('')
  const [building, setBuilding] = useState<string>('')
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [hasEatingArea, setHasEatingArea] = useState<string>('')
  const [temporaryHoliday, setTemporaryHoliday] = useState<string>('')
  const [homepage, setHomepage] = useState<string>('')
  const [twitter, setTwitter] = useState<string>('')
  const [instagram, setInstagram] = useState<string>('')
  const [facebook, setFacebook] = useState<string>('')
  const [line, setLine] = useState<string>('')
  const [status, setStatus] = useState<string>('')
  const [isDeleting, setDeleting] = useState<string[]>([])
  const [hashtags, setHashtags] = useState<string[]>([])
  const [description, setDescription] = useState<string>('')
  const [scheduledOpeningDate, setScheduledOpeningDate] = useState<string>('')
  const [holidays, setHolidays] = useState<DayOfWeekType[]>([])
  const [isAvailableOnApp, setIsAvailableOnApp] = useState<boolean>(false)
  const [isAvailableOnWeb, setIsAvailableOnWeb] = useState<boolean>(false)
  const [webHostName, setWebHostname] = useState<string>('')
  const [webLogoImageUrl, setWebLogoImageUrl] = useState<string>('')
  const [error, setError] = useState<string>('')

  const { center, position, generateStoreGeoSearch, handlePosition, storeGeoSearch } = useStoreGeoSearch(storeId)
  const { state: feeRate, handleState: handleFeeRate, setState: setFeeRate } = useSelect<string>('0.1')

  const handleName = useStringChangeEvent(setName)
  const handleNameKana = useStringChangeEvent(setNameKana)
  const handleDescription = useStringChangeEvent(setDescription)
  const handleFirstPostalCode = useStringChangeEvent(setFirstPostalCode)
  const handleSecondPostalCode = useStringChangeEvent(setSecondPostalCode)
  const handleCity = useStringChangeEvent(setCity)
  const handleStreet = useStringChangeEvent(setStreet)
  const handleBuilding = useStringChangeEvent(setBuilding)
  const handlePhoneNumber = useStringChangeEvent(setPhoneNumber)
  const handleHasEatingArea = useStringChangeEvent(setHasEatingArea)
  const handleTemporaryHoliday = useStringChangeEvent(setTemporaryHoliday)
  const handleHomepage = useStringChangeEvent(setHomepage)
  const handleTwitter = useStringChangeEvent(setTwitter)
  const handleInstagram = useStringChangeEvent(setInstagram)
  const handleFacebook = useStringChangeEvent(setFacebook)
  const handleLine = useStringChangeEvent(setLine)
  const handleWebHostName = useStringChangeEvent(setWebHostname)
  const handleIsAvailableOnApp = useToggleBoolean(setIsAvailableOnApp)
  const handleIsAvailableOnWeb = useToggleBoolean(setIsAvailableOnWeb)
  const handleHashTags = useCheckbox(setHashtags)
  const handleDeleting = useCheckbox(setDeleting)
  const handleScheduledOpeningDate = useStringChangeEvent(setScheduledOpeningDate)
  const selectState = useSelectCallback(setState)
  const handleWebLogoImageUrl = useSelectCallback(setWebLogoImageUrl)

  const {
    addBusinessTime,
    businessTimes,
    handleBusinessTimes,
    selectAllDaysOfWeek,
    selectDaysOfWeek,
    unselectAllDaysOfWeek,
  } = useBusinessTimes()

  const remainingDaysOfWeek = useRemainingDaysOfWeek(businessTimes, holidays)
  const selectableHolidays = useMemo(() => {
    return daysOfWeek.getDaysOfWeek().filter((dayOfWeek) => {
      // 選択中の曜日であれば残す
      if (holidays.includes(dayOfWeek)) return true
      // 営業時間として選択中の曜日は除外
      return !businessTimes.some((businessTime) => businessTime.days_of_week.includes(dayOfWeek))
    })
  }, [holidays, businessTimes])
  const handleHolidays = useDaysOfWeekCheckbox(setHolidays)
  const selectAllHolidays = useCallback(() => setHolidays([...selectableHolidays]), [setHolidays, selectableHolidays])
  const unselectAllHolidays = useCallback(() => setHolidays([]), [setHolidays])

  const fetchAddress = useCallback(
    async (postalCode: string) => {
      const data = await fetchAddressFromPostalCode(postalCode)
      if (!data || !data[0]) return
      const ja = data[0].ja
      setState(ja.prefecture ?? '')
      setCity(ja.address1 ?? '')
      setStreet(ja.address2 ?? '')
    },
    [setState, setCity, setStreet]
  )

  const selectStatus = useCallback(
    (value: string) => {
      if (stripeAccountId === '' && stripeAccountIdInStore === '' && value === 'public') {
        dispatch(
          openCautionModal(
            '',
            'キャンセル',
            'stripe アカウントIDが未設定のため公開できません。店舗アカウント情報の編集からstripe アカウントIDを設定してください。',
            () => null,
            'stripe アカウントIDが未設定です'
          )
        )
      } else {
        setStatus(value)
      }
    },
    [setStatus, stripeAccountId, stripeAccountIdInStore]
  )

  const isDuplicatedWebHostname = async () => {
    if (!webHostName) return false
    const webHostnames = await fetchWebHostnames()
    return webHostnames.includes(webHostName)
  }

  useEffect(() => {
    if (!store) return
    setImageUrls(store.image_urls ?? [])
    setName(store.name ?? '')
    setNameKana(store.name_kana ?? '')
    setFirstPostalCode(store.address ? store.address.postal_code.slice(0, 3) : '')
    setSecondPostalCode(store.address ? store.address.postal_code.slice(3, 7) : '')
    setState(store.address ? store.address.state : '')
    setCity(store.address ? store.address.city : '')
    setStreet(store.address ? store.address.street : '')
    setBuilding(store.address ? store.address.building : '')
    setPhoneNumber(store.phone_number ?? '')
    setHasEatingArea(store.has_eating_area ? '有り' : '無し')
    setHolidays(store.holidays ?? [])
    setTemporaryHoliday(store.temporary_holiday ?? '')
    const [_homepage, _twitter, _instagram, _facebook, _line] =
      store.urls && store.urls.length > 0
        ? store.urls.map((media: { [key: string]: string }) => media.url)
        : ['', '', '', '', '']
    setHomepage(_homepage)
    setTwitter(_twitter)
    setInstagram(_instagram)
    setFacebook(_facebook)
    setLine(_line)
    setHashtags(store.hashtags ?? [])
    setDescription(store.description ?? '')
    setStatus(store.status === 'deleted' ? '' : store.status)
    setDeleting(store.status === 'deleted' ? ['deleted'] : [])
    setScheduledOpeningDate(
      store.scheduled_opening_date ? format(store.scheduled_opening_date.toDate(), `yyyy-MM-dd'T'HH:mm`) : ''
    )
    setFeeRate(store.fee_rate ?? '0.1')
    setIsAvailableOnApp(store.is_available_on_app ?? true)
    setIsAvailableOnWeb(store.is_available_on_web ?? false)
    setWebHostname(store.web_hostname ?? '')
    setWebLogoImageUrl(store.web_logo_image_url ?? '')
  }, [store])

  useEffect(() => {
    dispatch(fetchTags('published'))
  }, [])

  const isFilledStoreInformation = isCompleteStoreInformation({
    imageUrls,
    name,
    nameKana,
    firstPostalCode,
    secondPostalCode,
    state,
    city,
    street,
    phoneNumber,
    hasEatingArea,
    status: isEventPage ? 'public' : status,
    homepage,
    twitter,
    instagram,
    facebook,
    line,
    feeRate,
    webHostName,
  })
  const isFilledStoreSettings = isCompleteStoreSettings({ businessTimes, remainingDaysOfWeek })
  const isFilled = isFilledStoreInformation && isFilledStoreSettings

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <MainHeadline headline={'店舗基本情報の登録'} />
      <Spacer size={28} />
      <FormLabel id={'image'} label={'店舗画像'} required={true} />
      <Spacer size={8} />
      <SubDescription description={'画像をドラッグアンドドロップしてもアップロードできます。'} />
      <Spacer size={20} />
      <ImagesCrop
        folderPath={`stores/${storeId}`}
        deleteImageUrls={deleteImageUrls}
        handleImageUrls={handleImageUrls}
        imageUrls={imageUrls.slice(0, 6)}
        rearrangeImages={rearrangeImages}
        imageType={'store'}
      />
      <Spacer size={28} />
      <TextInput disabled={true} label={'ID'} required={true} size={'small'} type={'text'} value={storeId} />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'name'}
        label={'店名'}
        onChange={handleName}
        placeholder={'店名を入力してください'}
        required={true}
        size={'small'}
        type={'text'}
        value={name}
      />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'name-kana'}
        label={'フリガナ'}
        onChange={handleNameKana}
        placeholder={'テンメイヲニュウリョクシテクダサイ'}
        required={true}
        size={'small'}
        type={'text'}
        value={nameKana}
      />
      <Spacer size={28} />
      <PostalCodeForm
        label={'郵便番号'}
        handleFirstPostalCode={handleFirstPostalCode}
        handleSecondPostalCode={handleSecondPostalCode}
        firstPostalCode={firstPostalCode}
        secondPostalCode={secondPostalCode}
      />
      <Spacer size={8} />
      <BlueButton
        disabled={firstPostalCode.length !== 3 || secondPostalCode.length !== 4}
        label={'住所を取得する'}
        onClick={() => fetchAddress(firstPostalCode + secondPostalCode)}
        size={'medium'}
        type={'main'}
      />
      <Spacer size={28} />
      <FormLabel id={'address'} label={'住所'} required={true} />
      <Spacer size={8} />
      <SelectBox options={stateOptions} select={selectState} value={state} />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        onChange={handleCity}
        placeholder={'市区町村'}
        required={true}
        size={'small'}
        type={'text'}
        value={city}
      />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        onChange={handleStreet}
        placeholder={'住所1'}
        required={true}
        size={'small'}
        type={'text'}
        value={street}
      />
      <Spacer size={8} />
      <TextInput
        disabled={false}
        onChange={handleBuilding}
        placeholder={'ビル名'}
        required={true}
        size={'small'}
        type={'text'}
        value={building}
      />
      <Spacer size={8} />
      <BlueButton
        disabled={state === '' || city === '' || street === ''}
        label={'緯度・経度を取得する'}
        onClick={() =>
          generateStoreGeoSearch({
            address: {
              postal_code: firstPostalCode + secondPostalCode,
              state: state,
              city: city,
              street: street,
              building: building,
            },
            image_urls: imageUrls,
            is_sacri_station: isEventPage,
            name: name,
            status: isDeleting.includes('deleted') ? 'deleted' : status,
          })
        }
        size={'medium'}
        type={'main'}
      />
      <Spacer size={28} />
      <FormLabel id={'map'} label={'緯度・経度'} required={true} />
      <Spacer size={8} />
      <SubDescription description={'アプリ上の表示位置を、ご自身で修正する場合は変更ください'} />
      <Spacer size={12} />
      <Map center={center} position={position} handlePosition={handlePosition} />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'phone-number'}
        label={'電話番号（ハイフンナシ）'}
        onChange={handlePhoneNumber}
        placeholder={'00000000000'}
        required={false}
        size={'small'}
        type={'tel'}
        value={phoneNumber}
      />
      <ErrorDescription
        isError={!isValidPhoneNumber(phoneNumber)}
        error={'電話番号は10桁か11桁の半角数字で入力してください'}
      />
      <Spacer size={28} />
      <FormLabel id={'eat-in'} label={'イートインスペースの有無'} required={true} />
      <Spacer size={8} />
      <RadioButtonsForm currentStatus={hasEatingArea} options={eatingAreaOptions} handleStatus={handleHasEatingArea} />
      <Spacer size={28} />
      <FormLabel id={'day-of-week'} label={'営業時間'} required={true} />
      <Spacer size={8} />
      <SubDescription description={'営業時間を入力する曜日を選択してください（複数選択可）'} />
      <Spacer size={12} />
      {businessTimes.map((businessTime, index) => {
        const selectableDaysOfWeek = daysOfWeek.getDaysOfWeek().filter((dayOfWeek) => {
          // 選択中の曜日であれば残す
          if (businessTime.days_of_week.includes(dayOfWeek)) return true
          // 定休日か別の営業時間カードで選択中の曜日は除外
          return (
            !holidays.includes(dayOfWeek) &&
            !businessTimes.some((_businessTime) => _businessTime.days_of_week.includes(dayOfWeek))
          )
        })
        const selectAll = () => selectAllDaysOfWeek(selectableDaysOfWeek, index)
        const unselectAll = () => unselectAllDaysOfWeek(index)
        return (
          <React.Fragment>
            <BusinessHourForm
              key={index.toString()}
              index={index}
              daysOfWeek={businessTime.days_of_week}
              openTime={businessTime.open_time}
              orderStartTime={businessTime.order_start_time}
              lastOrderTime={businessTime.last_order_time}
              lastPickupTime={businessTime.last_pickup_time}
              relativePickupableTime={businessTime.relative_pickupable_time}
              handleBusinessTimes={handleBusinessTimes}
              selectDaysOfWeek={selectDaysOfWeek}
              selectableDaysOfWeek={selectableDaysOfWeek}
              selectAll={selectAll}
              unselectAll={unselectAll}
            />
            <Spacer size={12} />
          </React.Fragment>
        )
      })}
      <TextButton
        disabled={
          remainingDaysOfWeek.length === 0 ||
          businessTimes.some((businessTime) => businessTime.days_of_week.length === 0)
        }
        iconPath={
          remainingDaysOfWeek.length === 0 ||
          businessTimes.some((businessTime) => businessTime.days_of_week.length === 0)
            ? '/icon/grey/plus.svg'
            : '/icon/blue/plus.svg'
        }
        label={'営業時間を追加する'}
        onClick={addBusinessTime}
      />
      <Spacer size={28} />
      <HolidayCard
        holidays={holidays}
        handleHolidays={handleHolidays}
        selectableDaysOfWeek={selectableHolidays}
        selectAll={selectAllHolidays}
        unselectAll={unselectAllHolidays}
      />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        label={'その他特別なお休み'}
        onChange={handleTemporaryHoliday}
        required={false}
        size={'small'}
        type={'text'}
        value={temporaryHoliday}
      />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'homepage'}
        label={'お店のホームページのURL'}
        onChange={handleHomepage}
        placeholder={'https://'}
        required={false}
        size={'small'}
        type={'text'}
        value={homepage}
      />
      <ErrorDescription isError={!isValidUrlString(homepage)} error={'URLの型式で入力してください'} />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'twitter'}
        label={'お店のtwitterのURL'}
        onChange={handleTwitter}
        placeholder={'https://'}
        required={false}
        size={'small'}
        type={'text'}
        value={twitter}
      />
      <ErrorDescription isError={!isValidUrlString(twitter)} error={'URLの型式で入力してください'} />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'instagram'}
        label={'お店のinstagramのURL'}
        onChange={handleInstagram}
        placeholder={'https://'}
        required={false}
        size={'small'}
        type={'text'}
        value={instagram}
      />
      <ErrorDescription isError={!isValidUrlString(instagram)} error={'URLの型式で入力してください'} />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'facebook'}
        label={'お店のfacebookのURL'}
        onChange={handleFacebook}
        placeholder={'https://'}
        required={false}
        size={'small'}
        type={'text'}
        value={facebook}
      />
      <ErrorDescription isError={!isValidUrlString(facebook)} error={'URLの型式で入力してください'} />
      <Spacer size={28} />
      <TextInput
        disabled={false}
        id={'line'}
        label={'お店のLINEアカウントのURL'}
        onChange={handleLine}
        placeholder={'https://'}
        required={false}
        size={'small'}
        type={'text'}
        value={line}
      />
      <ErrorDescription isError={!isValidUrlString(line)} error={'URLの型式で入力してください'} />
      <Spacer size={28} />
      <FormLabel label={'お店のこだわり'} required={false} />
      <Spacer size={8} />
      <div className={`${flex.flex__row_start} ${flex.flex__row_wrap}`}>
        {tags.map((tag) => (
          <TagCheckbox
            key={tag.name}
            id={tag.name}
            isChecked={hashtags.includes(tag.name)}
            onChange={handleHashTags}
            value={tag.name}
          />
        ))}
      </div>
      {!isEventPage && (
        <>
          <Spacer size={28} />
          <FormLabel label={'メディア用紹介文'} required={false} />
          <Spacer size={8} />
          <SubDescription
            description={'sacriのアプリ内や、ホームページ上で利用するお店の紹介文です（140字以内でご記入ください）'}
          />
          <Spacer size={8} />
          <Textarea
            disabled={false}
            maxLength={140}
            onChange={handleDescription}
            placeholder={'メディア掲載時の説明文を入力してください'}
            required={false}
            rows={5}
            value={description}
          />
        </>
      )}
      {isEditedByOwner && (
        <>
          <Spacer size={48} />
          <MainHeadline headline={'初回の焼き上がり配信開始日'} />
          <Spacer size={28} />
          <FormLabel label={'初回の焼き上がり配信開始日'} required={false} />
          <Spacer size={8} />
          <DatetimePicker onChange={handleScheduledOpeningDate} value={scheduledOpeningDate} />
        </>
      )}
      {/* 催事ではなく管理者であれば店舗の公開状態を変更できる */}
      {isAdmin && !isEventPage && (
        <>
          <Spacer size={48} />
          <MainHeadline headline={'sacriの管理者限定'} />
          <Spacer size={28} />
          <FormLabel label="手数料率" required={true} />
          <Spacer size={8} />
          <SelectBox options={FEE_RATE_OPTIONS} select={handleFeeRate} size={'full'} value={feeRate} />
          <Spacer size={28} />
          <FormLabel id={'status'} label={'公開状況'} required={true} />
          <Spacer size={8} />
          <SelectBox options={publishStatusOptions} select={selectStatus} value={status} />
          <Spacer size={28} />
          <ReleaseTargetForm
            isAvailableOnApp={isAvailableOnApp}
            isAvailableOnWeb={isAvailableOnWeb}
            webHostName={webHostName}
            webLogoImageUrl={webLogoImageUrl}
            handleIsAvailableOnApp={handleIsAvailableOnApp}
            handleIsAvailableOnWeb={handleIsAvailableOnWeb}
            handleWebHostName={handleWebHostName}
            handleWebLogoImageUrl={handleWebLogoImageUrl}
            isDomainDisabled={!!store.web_hostname}
            storeId={storeId}
          />
          <Spacer size={28} />
          <Checkbox
            id={'deleted'}
            isChecked={isDeleting.includes('deleted')}
            label={'このお店を削除する'}
            onChange={handleDeleting}
          />
          <Spacer size={8} />
          <SubCaution caution={'※削除したデータは元に戻すことができません'} />
        </>
      )}
      <Spacer size={48} />
      {isCreatePage ? (
        <BlueButton
          disabled={!isFilled}
          label={isEventPage ? '確認画面へ' : '登録する'}
          onClick={async () => {
            const storeStatus = isDeleting.includes('deleted') ? 'deleted' : status
            if (await isDuplicatedWebHostname()) {
              setError('他店で使用されていないドメインを入力してください')
              return
            }
            dispatch(
              saveStoreGeoSearch({
                imageUrls: imageUrls,
                status: storeStatus as StoreStatus,
                storeAddress: state + city + street + building,
                storeId,
                storeName: name,
                storeGeoSearch,
              })
            )
            dispatch(
              saveStoreInformation(
                {
                  address: {
                    postal_code: firstPostalCode + secondPostalCode,
                    state: state,
                    city: city,
                    street: street,
                    building: building,
                  },
                  business_hours: parseToOpenTime(businessTimes),
                  description: description,
                  fee_rate: Number(feeRate),
                  hashtags: hashtags,
                  has_eating_area: hasEatingArea === '有り',
                  has_schedule_types: store.has_schedule_types || [],
                  has_stock_types: store.has_stock_types || [],
                  holidays: holidays,
                  image_urls: imageUrls,
                  is_sacri_station: isEventPage,
                  last_order_time: parseToBusinessTime(businessTimes, 'last_order_time'),
                  last_pickup_time: parseToBusinessTime(businessTimes, 'last_pickup_time'),
                  name: name,
                  name_kana: nameKana,
                  open_time: store.open_time ?? '',
                  order_start_time: parseToBusinessTime(businessTimes, 'order_start_time'),
                  phone_number: phoneNumber,
                  relative_pickupable_time: parseToBusinessTime(businessTimes, 'relative_pickupable_time'),
                  scheduled_opening_date:
                    scheduledOpeningDate === '' ? null : FirebaseTimestamp.fromDate(new Date(scheduledOpeningDate)),
                  shop_ref: isEventPage ? undefined : db.collection('shops').doc(),
                  status: storeStatus,
                  shopping_bags: store.shopping_bags ?? [],
                  temporary_holiday: temporaryHoliday,
                  urls: [
                    { key: 'homepage', url: homepage },
                    { key: 'twitter', url: twitter },
                    { key: 'instagram', url: instagram },
                    { key: 'facebook', url: facebook },
                    { key: 'line', url: line },
                  ],
                  is_available_on_app: isAvailableOnApp,
                  is_available_on_web: isAvailableOnWeb,
                  web_hostname: webHostName,
                  web_logo_image_url: webLogoImageUrl,
                },
                storeId,
                isCreatePage,
                isEventPage,
                false,
                isEditedByOwner
              )
            )
          }}
          endIconPath={'/icon/white/arrow_next_small.svg'}
          size={'medium'}
          type={'main'}
        />
      ) : (
        <OrangeButton
          disabled={!isFilled}
          label={'確認する'}
          onClick={async () => {
            if ((await isDuplicatedWebHostname()) && store.web_hostname !== webHostName) {
              setError('他店で使用されていないドメインを入力してください')
              return
            }
            dispatch(saveStoreGeoSearchConfirm(storeGeoSearch))
            dispatch(
              saveStoreConfirm(
                {
                  address: {
                    postal_code: firstPostalCode + secondPostalCode,
                    state: state,
                    city: city,
                    street: street,
                    building: building,
                  },
                  business_hours: parseToOpenTime(businessTimes),
                  description: description,
                  fee_rate: Number(feeRate),
                  hashtags: hashtags,
                  has_eating_area: hasEatingArea === '有り',
                  has_schedule_types: store.has_schedule_types || [],
                  has_stock_types: store.has_stock_types || [],
                  holidays: holidays,
                  image_urls: imageUrls,
                  is_sacri_station: isEventPage,
                  last_order_time: parseToBusinessTime(businessTimes, 'last_order_time'),
                  last_pickup_time: parseToBusinessTime(businessTimes, 'last_pickup_time'),
                  name: name,
                  name_kana: nameKana,
                  open_time: store.open_time ?? '',
                  order_start_time: parseToBusinessTime(businessTimes, 'order_start_time'),
                  phone_number: phoneNumber,
                  relative_pickupable_time: parseToBusinessTime(businessTimes, 'relative_pickupable_time'),
                  scheduled_opening_date:
                    scheduledOpeningDate === '' ? null : FirebaseTimestamp.fromDate(new Date(scheduledOpeningDate)),
                  shop_ref: store.shop_ref,
                  shopping_bags: store.shopping_bags ?? [],
                  status: isDeleting.includes('deleted') ? 'deleted' : status,
                  temporary_holiday: temporaryHoliday,
                  urls: [
                    { key: 'homepage', url: homepage },
                    { key: 'twitter', url: twitter },
                    { key: 'instagram', url: instagram },
                    { key: 'facebook', url: facebook },
                    { key: 'line', url: line },
                  ],
                  is_available_on_app: isAvailableOnApp,
                  is_available_on_web: isAvailableOnWeb,
                  web_hostname: webHostName,
                  web_logo_image_url: webLogoImageUrl,
                },
                storeId,
                isEditedByOwner
              )
            )
          }}
          size={'medium'}
          type={'main'}
        />
      )}
      <ErrorDescription isError={!!error} error={error} />
      {!isFilled && <Spacer size={24} />}
      {!isFilledStoreInformation && (
        <StoreInfoError
          imageUrls={imageUrls}
          name={name}
          nameKana={nameKana}
          firstPostalCode={firstPostalCode}
          secondPostalCode={secondPostalCode}
          state={state}
          city={city}
          street={street}
          phoneNumber={phoneNumber}
          hasEatingArea={hasEatingArea}
          status={status}
          homepage={homepage}
          twitter={twitter}
          instagram={instagram}
          facebook={facebook}
          line={line}
        />
      )}
      {!isFilledStoreSettings && (
        <StoreSettingError businessTimes={businessTimes} remainingDaysOfWeek={remainingDaysOfWeek} />
      )}
      {isCreatePage && !isEventPage && (
        <>
          <Spacer size={12} />
          <NavButton
            label={'店舗アカウント情報の設定に戻る'}
            iconPath={'/icon/grey/arrow_prev.svg'}
            url={'/admin/shops/create/account'}
          />
        </>
      )}
      <Spacer size={48} />
      <NavButton
        label={'トップページに戻る'}
        iconPath={'/icon/grey/home.svg'}
        onClick={() => {
          if (isEditedByOwner) {
            dispatch(push('/'))
          } else {
            dispatch(push('/admin/shops'))
            dispatch(initConfirm())
            dispatch(initStore())
            dispatch(initShop())
            dispatch(initStocks())
            dispatch(initBaskets())
          }
        }}
        url={''}
      />
      <Spacer size={72} />
    </div>
  )
}

export default ShopEditInformationTemplate
